if (!customElements.get('image-text-showcase')) {
  customElements.define(
    'image-text-showcase',
    class ImageTextShowcase extends HTMLElement {
      connectedCallback() {
        this.initSlider();
        this.initScrollAnimation();
      }

      disconnectedCallback() {
        if (this.onScroll) {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
          window.removeEventListener('load', this.onScroll);
        }
        if (this.splide) this.splide.destroy();
      }

      // ---- Slider (Splide) ----

      initSlider() {
        if (this.dataset.slider !== 'true') return;

        this.sliderElement = this.querySelector('.splide');
        if (!this.sliderElement) return;

        if (window.Splide) {
          this.mountSplide();
        } else {
          // Splide loads with `defer` from a CDN; if this runs first, wait for it.
          window.addEventListener('load', () => this.mountSplide(), { once: true });
        }

        // In the theme editor, jump to a block when it is selected.
        this.addEventListener('shopify:block:select', (event) => {
          const slide = event.target.closest('.hit__slide');
          const slides = Array.from(this.querySelectorAll('.hit__slide:not(.splide__slide--clone)'));
          const index = slides.indexOf(slide);
          if (this.splide && index > -1) this.splide.go(index);
        });
      }

      mountSplide() {
        if (!window.Splide || this.splide || !this.sliderElement) return;

        const data = this.dataset;
        const type = data.sliderType || 'slide';
        const perPage = type === 'fade' ? 1 : parseInt(data.perPage, 10) || 1;
        const perPageMobile = type === 'fade' ? 1 : parseInt(data.perPageMobile, 10) || 1;

        this.splide = new window.Splide(this.sliderElement, {
          type,
          perPage,
          gap: `${parseInt(data.gap, 10) || 0}px`,
          speed: parseInt(data.speed, 10) || 600,
          rewind: type === 'slide',
          drag: data.drag === 'true',
          arrows: data.arrows === 'true',
          pagination: data.pagination === 'true',
          autoplay: data.autoplay === 'true',
          interval: parseInt(data.interval, 10) || 5000,
          pauseOnHover: data.pauseOnHover === 'true',
          breakpoints: { 749: { perPage: perPageMobile } },
        });

        // Splide clones slides for the loop type, so blocks are looked up again on every update.
        this.splide.on('mounted resized', () => this.requestUpdate && this.requestUpdate());
        this.splide.mount();
      }

      // ---- Scroll animation ----

      initScrollAnimation() {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.dataset.animate !== 'true' || reducedMotion) return;

        this.start = parseFloat(this.dataset.start) || 0;
        this.end = parseFloat(this.dataset.end) || 0.5;
        this.textDelay = parseFloat(this.dataset.textDelay) || 0;
        this.easing = this.dataset.easing || 'ease-out';
        this.ticking = false;

        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.update();
            this.ticking = false;
          });
        };
        this.requestUpdate = this.onScroll;

        this.update();
        this.classList.add('hit--ready');

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        window.addEventListener('load', this.onScroll);
      }

      ease(t) {
        if (this.easing === 'linear') return t;
        if (this.easing === 'ease-in-out') return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        return 1 - Math.pow(1 - t, 3);
      }

      update() {
        const vh = window.innerHeight;

        this.querySelectorAll('.hit__block:not(.hit__block--static)').forEach((block) => {
          const rect = block.getBoundingClientRect();
          const center = rect.top + rect.height / 2;
          // 0 when the block has just entered (start), 1 when its middle reaches the end line.
          const startCenter = vh - rect.height * this.start + rect.height / 2;
          const endCenter = vh * this.end;
          const range = startCenter - endCenter || 1;
          const raw = Math.min(1, Math.max(0, (startCenter - center) / range));

          const text = Math.min(1, Math.max(0, (raw - this.textDelay) / (1 - this.textDelay || 1)));

          block.style.setProperty('--hit-p', this.ease(raw).toFixed(4));
          block.style.setProperty('--hit-pt', this.ease(text).toFixed(4));
        });
      }
    }
  );
}
